import uuid from 'uuid/v4';
import get from 'lodash/get';
import find from 'lodash/find';
import pick from 'lodash/pick';

import Catalyst from '@Services/Catalyst';
import ExportRequestManager from '@Services/ExportRequestManager';
import RXService from '@Services/RxService';
import { EXPORT_TAB_EVENTS } from '@Services/ExportService/ExportConstants';
import { exportTabSelectors, chemquoteExportTabSelectors } from './ExportTabSelectors';

import { EXPORT_TAB_RESULT_STATUSES, exportTabActionsTypes, chemQuoteExportTabActionsTypes } from './ExportTabConstants';

export const createActions = (selectors, actionsTypes) => {
  const SAVED_EXPORTS = {};

  const {
    exportTabSetExportTypeReducerType,
    exportTabSetExportResultReducerType,
    exportTabSetRetryDisabledReducerType,
    exportTabSetDownloadUrlReducerType,
    exportTabSetTooltipModalOpenedReducerType,
    exportTabSetCancelledJobIdsReducerType,
  } = actionsTypes;

  const {
    exportResult: exportResultSelector,
    exportType: exportTypeSelector,
    downloadUrl: downloadUrlSelector,
    cancelledJobIds: cancelledJobIdsSelector,
  } = selectors;

  const setExportType = (exportType) => {
    return {
      type: exportTabSetExportTypeReducerType,
      payload: { exportType },
    };
  };

  const setExportResult = (exportResult) => {
    return {
      type: exportTabSetExportResultReducerType,
      payload: { exportResult },
    };
  };

  const setRetryDisabled = (retryDisabled) => {
    return {
      type: exportTabSetRetryDisabledReducerType,
      payload: { retryDisabled },
    };
  };

  const setDownloadUrl = (downloadUrl) => {
    return {
      type: exportTabSetDownloadUrlReducerType,
      payload: { downloadUrl },
    };
  };

  const setTooltipModalOpened = (tooltipModalOpened) => {
    return {
      type: exportTabSetTooltipModalOpenedReducerType,
      payload: { tooltipModalOpened },
    };
  };

  const setCancelledJobIds = (cancelledJobIds) => {
    return {
      type: exportTabSetCancelledJobIdsReducerType,
      payload: { cancelledJobIds },
    };
  };

  const exportStarted = (exportSubject) => {
    return (dispatch, getState) => {
      const exportType = exportTypeSelector(getState());

      if (!SAVED_EXPORTS.exportResult) {
        SAVED_EXPORTS.exportResult = {};
      }

      const result = {
        status: EXPORT_TAB_RESULT_STATUSES.STARTED,
        cancelJob: exportSubject.cancel,
      };

      SAVED_EXPORTS.exportResult[exportType] = result;
      dispatch(setExportResult(result));
      dispatch(setTooltipModalOpened(true));
    };
  };

  const progressInfoHandler = (response) => {
    return (dispatch, getState) => {
      const exportType = exportTypeSelector(getState());
      const cancelledJobIds = cancelledJobIdsSelector(getState());

      // eslint-disable-next-line prefer-const
      let { jobId, percentage: progress, document: documentName } = get(response, 'data.payload', {});

      // Case for ChemQuote when there is no progress
      if (progress === undefined) {
        progress = 0;
      }

      const status = progress >= 100 ? EXPORT_TAB_RESULT_STATUSES.FINISHED : EXPORT_TAB_RESULT_STATUSES.RUNNING;

      if (!jobId || find(cancelledJobIds, (cancelledJobId) => cancelledJobId === jobId)) {
        return;
      }

      // I go ahead with UI updates and other actions only if the jobId is
      // not present in the cancelledJobIds array list.

      // first progress - open dropdown
      if (!SAVED_EXPORTS.exportResult[exportType]) {
        dispatch(setTooltipModalOpened(true));
      }

      const result = {
        jobId,
        status,
        progress,
        documentName,
        cancelJob: response.cancel,
      };

      SAVED_EXPORTS.exportResult[exportType] = result;
      dispatch(setExportResult(result));

      if (status !== EXPORT_TAB_RESULT_STATUSES.FINISHED) {
        return;
      }

      const url = ExportRequestManager.getFileDownloadUrl(exportType, result);
      dispatch(setDownloadUrl(url));
      response.cancel();
    };
  };

  const cancel = (becauseOfError = false, skipCancelRequest = false) => {
    return (dispatch, getState) => {
      const exportType = exportTypeSelector(getState());
      const cancelledJobIds = cancelledJobIdsSelector(getState());

      dispatch(setTooltipModalOpened(false));

      const cancelJob = get(SAVED_EXPORTS, `exportResult.${exportType}.cancelJob`);
      if (!cancelJob) {
        ExportRequestManager.cleanupCurrentRequestByType(exportType);
        return;
      }

      if (!becauseOfError) {
        const jobId = get(SAVED_EXPORTS, `exportResult.${exportType}.jobId`);
        dispatch(setCancelledJobIds([...cancelledJobIds, jobId]));

        // Add current ID in the list of cancelled
        if (!skipCancelRequest) {
          cancelJob(jobId);
        }
      }
      // eslint-disable-next-line no-param-reassign,no-multi-assign
      SAVED_EXPORTS.exportResult[exportType] = null;
      dispatch(setExportResult(null));

      ExportRequestManager.cleanupCurrentRequestByType(exportType);
    };
  };

  const downloadByURL = () => {
    return (dispatch, getState) => {
      const exportType = exportTypeSelector(getState());
      const downloadUrl = downloadUrlSelector(getState());

      // AA tracking - get tracking content object
      let trackingContent = ExportRequestManager.getTrackingByExportType(exportType);

      if (trackingContent) {
        // Pick only required fields from tracking content object
        trackingContent = pick(trackingContent, ['id', 'type', 'format']);
      } else {
        // If unable to get tracking content object from service - create it
        const reg = /(?:\.([^.]+))?$/; // RegExp to get file extension for format field
        const extension = reg.exec(downloadUrl)[1];

        trackingContent = {
          id: `export:${uuid()}`,
          type: 'REAXYS-EXPORT',
          format: extension && extension.toUpperCase(),
        };
      }

      // AA tracking - track
      Catalyst.trackExportDownload(trackingContent);

      // Open download link
      window.open(downloadUrl, '_blank');
      dispatch(cancel(false, true));
    };
  };

  const _errorConsumer = (err, newSubject) => {
    return (dispatch, getState) => {
      const exportResult = exportResultSelector(getState());

      dispatch(setExportResult({ ...exportResult, status: EXPORT_TAB_RESULT_STATUSES.CONNECTION_ERROR }));
      dispatch(setRetryDisabled(false));
      dispatch(setTooltipModalOpened(true));

      if (newSubject) {
        newSubject.cancel();
      }

      // Track error
      Catalyst.trackError(
        {
          ...err,
          longMessage: 'Your connection to the server was interrupted',
          component: 'Export',
        },
        'se-'
      );
    };
  };

  const restore = () => {
    Logger.info('Restore export');
    return (dispatch, getState) => {
      const exportType = exportTypeSelector(getState());
      const exportResult = exportResultSelector(getState());

      if (!exportResult) {
        // nothing to restore
        return;
      }

      const newSubject = ExportRequestManager.restoreExport(exportType, exportResult.jobId, (err) => {
        // Error - transfer to connection-error status
        dispatch(_errorConsumer(err, newSubject));
      });
    };
  };

  const connectionErrorHandler = (canRestore) => {
    return (dispatch, getState) => {
      const exportResult = exportResultSelector(getState());
      const status = get(exportResult, 'status');

      // if finished or not started - do nothing
      if (!exportResult || status === EXPORT_TAB_RESULT_STATUSES.FINISHED) {
        return;
      }

      const jobId = get(exportResult, 'jobId');

      // if we have jobId and not already in connection-error status - try to restore
      if (canRestore && jobId && status !== EXPORT_TAB_RESULT_STATUSES.CONNECTION_ERROR) {
        // Don't remove timeout, it allows restore to be called after RxSocket.onClose internal processings
        dispatch(restore());
        return;
      }
      // otherwise transfer to connection-error status
      dispatch(_errorConsumer());
    };
  };

  const retry = () => {
    return (dispatch, getState) => {
      const exportType = exportTypeSelector(getState());
      dispatch(setRetryDisabled(true));
      ExportRequestManager.sendExportRequest(exportType);
    };
  };

  const initializeTab = ({ exportType, canRestore }) => {
    return (dispatch) => {
      dispatch(setExportType(exportType));
      RXService.subscribe(EXPORT_TAB_EVENTS[exportType].exportStarted, (exportSubject) => dispatch(exportStarted(exportSubject)));
      RXService.subscribe(EXPORT_TAB_EVENTS[exportType].progressInfo, (response) => dispatch(progressInfoHandler(response)));
      RXService.subscribe(EXPORT_TAB_EVENTS[exportType].cancel, () => dispatch(cancel(true)));
      RXService.subscribe(EXPORT_TAB_EVENTS[exportType].connectionError, () => dispatch(connectionErrorHandler(canRestore)));
    };
  };

  const closeTab = () => {
    return (dispatch, getState) => {
      const exportType = exportTypeSelector(getState());
      if (SAVED_EXPORTS.exportResult) {
        // eslint-disable-next-line no-param-reassign
        delete SAVED_EXPORTS.exportResult[exportType];
      }
      RXService.unsubscribe(EXPORT_TAB_EVENTS[exportType].exportStarted);
      RXService.unsubscribe(EXPORT_TAB_EVENTS[exportType].cancel);
      RXService.unsubscribe(EXPORT_TAB_EVENTS[exportType].progressInfo);
      RXService.unsubscribe(EXPORT_TAB_EVENTS[exportType].connectionError);
    };
  };

  return {
    initializeTab,
    closeTab,
    retry,
    connectionErrorHandler,
    restore,
    _errorConsumer,
    downloadByURL,
    cancel,
    progressInfoHandler,
    exportStarted,
    setExportType,
    setExportResult,
    setRetryDisabled,
    setDownloadUrl,
    setTooltipModalOpened,
    setCancelledJobIds,
    SAVED_EXPORTS,
  };
};

export const exportTabActions = createActions(exportTabSelectors, exportTabActionsTypes);
export const chemquoteExportActions = createActions(chemquoteExportTabSelectors, chemQuoteExportTabActionsTypes);
